import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { plannerNotesService, type NoteFile, type NoteFileList } from '@/services/plannerNotesService';

const filesKey = (tenantId?: string | null) => ['planner-note-files', tenantId] as const;

/** Largest attachment the backend accepts (bytes). */
export const NOTE_FILE_MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'png', 'jpg', 'jpeg', 'gif', 'webp',
]);

/** The client's attachments: the files in its Drive folder, newest first, plus the folder link. */
export function useNoteFiles(tenantId?: string | null, enabled = false) {
  return useQuery({
    queryKey: filesKey(tenantId),
    queryFn: () => {
      if (!tenantId) throw new Error('Tenant is required');
      return plannerNotesService.listFiles(tenantId);
    },
    enabled: Boolean(tenantId) && enabled,
    staleTime: 15_000,
  });
}

export function useDeleteNoteFile(tenantId?: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: NoteFile) => {
      if (!tenantId) throw new Error('Tenant is required');
      return plannerNotesService.deleteFile(tenantId, file.id);
    },
    onSuccess: (_data, file) => {
      // Drop the row from the cached list at once so it cannot flash back, then
      // refetch in the background (not awaited: the dialog closes now).
      qc.setQueryData<NoteFileList>(filesKey(tenantId), (old) =>
        old ? { ...old, files: old.files.filter((f) => f.id !== file.id) } : old);
      void qc.invalidateQueries({ queryKey: filesKey(tenantId) });
    },
    onError: (_err, file) => message.error(`Could not remove ${file.name}. Try again.`),
  });
}

/** One file being uploaded (or refused / failed), shown as a row until it is done or dismissed. */
export interface PendingUpload {
  key: string;
  name: string;
  status: 'queued' | 'uploading' | 'error';
  percent: number;
  error?: string;
}

/** Why an upload failed, from the HTTP status (the server's detail is not shown). */
export function uploadErrorText(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 413) return 'Too big (10 MB maximum)';
  if (status === 415) return 'This file type is not allowed';
  if (status === 503) return 'Attachments are not configured';
  return 'Upload failed, try again';
}

/**
 * Uploads files one at a time into the client's Drive folder and tracks each as a row with its
 * own progress. Lives above the editor dialog, so an upload keeps going if the dialog closes.
 */
export function useNoteUploads(tenantId?: string | null, enabled = false) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const busyRef = useRef(false);
  const seq = useRef(0);

  const patch = useCallback((key: string, change: Partial<PendingUpload>) => {
    setPending((rows) => rows.map((r) => (r.key === key ? { ...r, ...change } : r)));
  }, []);

  const dismiss = useCallback((key: string) => {
    setPending((rows) => rows.filter((r) => r.key !== key));
  }, []);

  const upload = useCallback(async (files: File[]) => {
    if (!tenantId || !enabled || !files.length) return;
    // Refuse what the server would refuse, right on the row; the rest still uploads.
    const rows = files.map((file) => {
      seq.current += 1;
      const ext = (file.name.split('.').pop() ?? '').toLowerCase();
      const tooBig = file.size > NOTE_FILE_MAX_BYTES;
      const badType = !file.name.includes('.') || !ALLOWED_EXT.has(ext);
      const error = tooBig ? 'Too big (10 MB maximum)' : badType ? 'This file type is not allowed' : undefined;
      const row: PendingUpload = {
        key: `u${seq.current}`, name: file.name, percent: 0,
        status: error ? 'error' : 'queued', error,
      };
      return { file, row };
    });
    setPending((cur) => [...cur, ...rows.map((r) => r.row)]);
    // One batch at a time: later drops wait for their turn instead of racing.
    while (busyRef.current) await new Promise((r) => setTimeout(r, 50));
    busyRef.current = true;
    try {
      for (const { file, row } of rows) {
        if (row.status === 'error') continue;
        patch(row.key, { status: 'uploading' });
        try {
          await plannerNotesService.uploadFile(tenantId, file, (percent) => patch(row.key, { percent }));
          await qc.invalidateQueries({ queryKey: filesKey(tenantId) });
          dismiss(row.key);
        } catch (err) {
          patch(row.key, { status: 'error', error: uploadErrorText(err) });
        }
      }
    } finally {
      busyRef.current = false;
    }
  }, [tenantId, enabled, qc, patch, dismiss]);

  const uploading = pending.some((p) => p.status === 'queued' || p.status === 'uploading');
  return { pending, uploading, upload, dismiss };
}

export type NoteUploads = ReturnType<typeof useNoteUploads>;
